import { useMemo } from 'react';

import { UxGoodsCardItemForVerticalProductCard as UxGoodsCardItem } from '@common/product-card';
import { formatCategoryList, useInitLikedProducts, useLikeProductService } from '@widgets/like-product';
import {
  parseQueryString,
  PDPGoodsGroup_goods_carousel_component_list as ProductCardItem,
  useCatalogProductIdParams,
  usePdpBaseInfo,
  usePdpRelatedProductRecommendGroups,
} from '@domains/pdp-common';

import { listInfoConfig } from '../components/carousel/RelatedRecommendCarousels';
import { useBottomRecommendListLog } from '../log/useBottomRecommendListLog';
import { useRecommendTabLog } from '../log/useRecommendTabLog';

const convertList = (list: ProductCardItem[]) => list.map(({ catalog_product_id }) => catalog_product_id || '');

export const useRecommendTabService = (productId: string) => {
  const params = useCatalogProductIdParams();
  const {
    flags: { is_selected_shop },
  } = usePdpBaseInfo({ catalog_product_id: productId });
  const { bottom_area } = usePdpRelatedProductRecommendGroups({
    catalog_product_id: params.catalog_product_id,
    isSelectedShop: Boolean(is_selected_shop),
  });
  const { impressionProductItem, clickProductItem, savedProduct, viewMore } = useRecommendTabLog(productId);
  const { clickSelectedMoreViewUBL } = useBottomRecommendListLog(productId);
  const { onLikeProduct } = useLikeProductService();

  const itemList = useMemo(
    () => bottom_area.flatMap((item) => item.goods_carousel.component_list) || [],
    [bottom_area],
  );

  useInitLikedProducts({
    data: itemList,
    convert: convertList,
  });

  const carouselsList = bottom_area;

  const onImpressionProduct =
    ({ groupType, position }: listInfoConfig) =>
    (item: UxGoodsCardItem) =>
      impressionProductItem({ position, groupType, item });

  const onClickProduct =
    ({ groupType, position }: listInfoConfig) =>
    (item: UxGoodsCardItem) =>
      clickProductItem({ position, groupType, item });

  const onSavedProduct =
    ({ groupType, position }: listInfoConfig) =>
    (checked: boolean, item: UxGoodsCardItem) => {
      const inAppLikeProductLog = {
        object_idx: position,
        object_section: 'subtab_recommendation',
        ...{
          original_product_id: productId,
          recommend_type: convertRecommendType(groupType),
          ...(item.log ? parseQueryString(item.log) : undefined),
        },
      };

      const inAppLikeProductPayload = {
        like: checked,
        browsingType: item.browsing_type,
        shopId: Number(item.shop_id),
        shopProductNo: item.shop_product_no,
        catalogProductId: item.catalog_product_id,
        title: item.title || '',
        goodsUrl: item.product_url || '',
        imageUrl: item.image_url || '',
        price: Number(item.final_price),
        discountRate: Number(item.discount_rate),
        isZpay: Boolean(item.zpay),
        isFreeShipping: Boolean(item.free_shipping),
        log: inAppLikeProductLog,
      };

      const airbridge = {
        brandID: item.shop_id,
        brandName: item.shop_name,
        productID: item.catalog_product_id,
        productName: item.title,
        price: item.final_price,
        categoryID: formatCategoryList(item.managed_category_list, 'id'),
        categoryName: formatCategoryList(item.managed_category_list, 'value'),
      };

      const webLikeProductPayload = {
        checked,
        productData: { catalog_product_id: item.catalog_product_id || '', shop_id: item.shop_id || '' },
        airbridge,
        sendLog: (checked: boolean) =>
          savedProduct({
            category: checked ? 'save_product' : 'remove_saved_product',
            position: position,
            groupType,
            item,
          }),
      };

      onLikeProduct({ inAppLikeProductPayload, webLikeProductPayload });
    };

  const onClickViewMore = () => {
    if (is_selected_shop) {
      clickSelectedMoreViewUBL();
      return;
    }
    viewMore();
  };

  return { carouselsList, onImpressionProduct, onClickProduct, onSavedProduct, onClickViewMore };
};

function convertRecommendType(str: string) {
  const data = str.replace('_item_list', '').replace('store_', '');
  if (str.indexOf('purchase') > -1) {
    return data.replace('related_', '');
  } else {
    return data;
  }
}
